import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Pencil, Trash2, BookOpen, Globe, Tag, Search } from 'lucide-react';

interface AgentKnowledge {
  id: string;
  company_config_id: string;
  category: string;
  title: string;
  content: string;
  tags: string[];
  priority: number;
  is_active: boolean;
  metadata?: any;
  created_at: string;
  updated_at: string;
  company_config?: {
    name: string;
  } | null;
}

interface GeneralKnowledge {
  id: string;
  category: string;
  title: string;
  content: string;
  tags: string[];
  priority: number;
  is_active: boolean;
  metadata: any;
  created_at: string;
  updated_at: string;
}

interface Agent {
  id: string;
  name: string;
}

const CATEGORIES = [
  'company_info',
  'services',
  'pricing',
  'hours',
  'team',
  'locations',
  'policies',
  'emergency',
  'seasonal',
  'promotions',
  'technical',
  'other'
];

const AgentKnowledgeBasePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'agent' | 'general'>('agent');
  const [agentKnowledge, setAgentKnowledge] = useState<AgentKnowledge[]>([]);
  const [generalKnowledge, setGeneralKnowledge] = useState<GeneralKnowledge[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AgentKnowledge | GeneralKnowledge | null>(null);
  
  // Filters
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    company_config_id: '',
    category: 'company_info',
    title: '',
    content: '',
    tags: [] as string[],
    priority: 5,
    is_active: true,
    metadata: {}
  });

  useEffect(() => {
    fetchAgents();
    if (activeTab === 'agent') {
      fetchAgentKnowledge();
    } else {
      fetchGeneralKnowledge();
    }
  }, [activeTab]);

  const fetchAgents = async () => {
    const { data, error } = await supabase
      .from('company_configs')
      .select('id, company_name')
      .eq('is_active', true)
      .order('company_name');

    if (error) {
      console.error('Error fetching agents:', error);
    } else {
      setAgents(data?.map((a: any) => ({ id: a.id, name: a.company_name })) || []);
    }
  };

  const fetchAgentKnowledge = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('agent_knowledge_base')
      .select(`
        *,
        company_config:company_configs!agent_knowledge_base_company_config_id_fkey(name:company_name)
      `)
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching agent knowledge:', error);
    } else {
      setAgentKnowledge(data || []);
    }
    setLoading(false);
  };

  const fetchGeneralKnowledge = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('general_knowledge_base')
      .select('*')
      .order('priority', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching general knowledge:', error);
    } else {
      setGeneralKnowledge(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const dataToSave = {
      ...formData,
      updated_at: new Date().toISOString()
    };

    if (activeTab === 'agent') {
      if (editingItem && 'company_config_id' in editingItem) {
        const { error } = await supabase
          .from('agent_knowledge_base')
          .update(dataToSave)
          .eq('id', editingItem.id);

        if (error) {
          console.error('Error updating:', error);
          alert('Failed to update knowledge');
        } else {
          await fetchAgentKnowledge();
          closeModal();
        }
      } else {
        const { error } = await supabase
          .from('agent_knowledge_base')
          .insert([{ ...dataToSave, created_at: new Date().toISOString() }]);

        if (error) {
          console.error('Error creating:', error);
          alert('Failed to create knowledge');
        } else {
          await fetchAgentKnowledge();
          closeModal();
        }
      }
    } else {
      // General knowledge
      const { company_config_id, ...generalData } = dataToSave;
      
      if (editingItem) {
        const { error } = await supabase
          .from('general_knowledge_base')
          .update(generalData)
          .eq('id', editingItem.id);

        if (error) {
          console.error('Error updating:', error);
          alert('Failed to update knowledge');
        } else {
          await fetchGeneralKnowledge();
          closeModal();
        }
      } else {
        const { error } = await supabase
          .from('general_knowledge_base')
          .insert([{ ...generalData, created_at: new Date().toISOString() }]);

        if (error) {
          console.error('Error creating:', error);
          alert('Failed to create knowledge');
        } else {
          await fetchGeneralKnowledge();
          closeModal();
        }
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this knowledge entry?')) return;

    const table = activeTab === 'agent' ? 'agent_knowledge_base' : 'general_knowledge_base';
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting:', error);
      alert('Failed to delete knowledge');
    } else {
      if (activeTab === 'agent') {
        await fetchAgentKnowledge();
      } else {
        await fetchGeneralKnowledge();
      }
    }
  };

  const openModal = (item?: AgentKnowledge | GeneralKnowledge) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        company_config_id: 'company_config_id' in item ? item.company_config_id : '',
        category: item.category,
        title: item.title,
        content: item.content,
        tags: item.tags || [],
        priority: item.priority,
        is_active: item.is_active,
        metadata: item.metadata || {}
      });
    } else {
      setEditingItem(null);
      setFormData({
        company_config_id: agents[0]?.id || '',
        category: 'company_info',
        title: '',
        content: '',
        tags: [],
        priority: 5,
        is_active: true,
        metadata: {}
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const getCategoryLabel = (category: string) => {
    return category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      company_info: 'bg-blue-100 text-blue-800',
      services: 'bg-green-100 text-green-800',
      pricing: 'bg-purple-100 text-purple-800',
      hours: 'bg-yellow-100 text-yellow-800',
      team: 'bg-pink-100 text-pink-800',
      locations: 'bg-indigo-100 text-indigo-800',
      policies: 'bg-red-100 text-red-800',
      emergency: 'bg-orange-100 text-orange-800',
      seasonal: 'bg-teal-100 text-teal-800',
      promotions: 'bg-cyan-100 text-cyan-800',
      technical: 'bg-gray-100 text-gray-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const filteredData = (): (AgentKnowledge | GeneralKnowledge)[] => {
    let data: (AgentKnowledge | GeneralKnowledge)[] = activeTab === 'agent' ? agentKnowledge : generalKnowledge;
    
    if (activeTab === 'agent' && selectedAgent !== 'all') {
      data = (data as AgentKnowledge[]).filter(item => item.company_config_id === selectedAgent) as (AgentKnowledge | GeneralKnowledge)[];
    }
    
    if (selectedCategory !== 'all') {
      data = data.filter(item => item.category === selectedCategory) as (AgentKnowledge | GeneralKnowledge)[];
    }
    
    if (searchQuery) {
      data = data.filter(item => 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      ) as (AgentKnowledge | GeneralKnowledge)[];
    }
    
    return data;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Knowledge Base Manager</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage agent-specific and general knowledge for AI voice agents
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Add Knowledge
        </button>
      </div>

      <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900">
        <p className="text-sm">
          About this page: This is the legacy knowledge manager backed by <span className="font-mono">agent_knowledge_base</span> (per-company) and <span className="font-mono">general_knowledge_base</span> (global).
          The modern, unified knowledge system lives on the{' '}
          <a href="/ai-voice-agents/knowledge-base" className="underline font-medium">Knowledge Base</a> page and is backed by <span className="font-mono">knowledge_articles</span>.
          Prefer creating and curating content in the unified Knowledge Base. Keep this page for legacy records and staged migration.
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('agent')}
              className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium transition-colors ${
                activeTab === 'agent'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <BookOpen size={18} />
              Agent-Specific Knowledge
            </button>
            <button
              onClick={() => setActiveTab('general')}
              className={`flex items-center gap-2 px-6 py-3 border-b-2 font-medium transition-colors ${
                activeTab === 'general'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Globe size={18} />
              General Knowledge
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {activeTab === 'agent' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Agent
                </label>
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="all">All Agents</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>{agent.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="all">All Categories</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search knowledge..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading knowledge...</p>
        </div>
      ) : filteredData().length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No knowledge entries found. Create your first entry to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredData().map(item => (
            <div
              key={item.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(item.category)}`}>
                    {getCategoryLabel(item.category)}
                  </span>
                  {!item.is_active && (
                    <span className="ml-2 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                      Inactive
                    </span>
                  )}
                  <h3 className="font-semibold text-gray-900 mt-2">{item.title}</h3>
                  {activeTab === 'agent' && 'company_config' in item && (
                    <p className="text-xs text-gray-500 mt-1">
                      {(item as AgentKnowledge).company_config?.name || 'Unknown Agent'}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openModal(item)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-4">
                {item.content}
              </p>

              {item.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {item.tags.slice(0, 3).map((tag: string, idx: number) => (
                    <span key={idx} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      <Tag size={12} />
                      {tag}
                    </span>
                  ))}
                  {item.tags.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{item.tags.length - 3} more
                    </span>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center text-xs text-gray-500 pt-3 border-t">
                <span>Priority: {item.priority}</span>
                <span>Updated {new Date(item.updated_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingItem ? 'Edit Knowledge' : 'Add New Knowledge'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {activeTab === 'agent' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Agent *
                      </label>
                      <select
                        required
                        value={formData.company_config_id}
                        onChange={(e) => setFormData({ ...formData, company_config_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select Agent</option>
                        {agents.map(agent => (
                          <option key={agent.id} value={agent.id}>{agent.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., Service Area Coverage"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content *
                    </label>
                    <textarea
                      required
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows={6}
                      placeholder="Detailed information that agents should know..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.tags.join(', ')}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., service-area, coverage, emergency"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (1-10) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="10"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Higher priority knowledge is presented first (10 = highest)
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                      Knowledge is Active
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingItem ? 'Update Knowledge' : 'Create Knowledge'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentKnowledgeBasePage;
